import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ShoppingBag, X, Plus, Minus, Check, ShoppingCart, Filter, Sparkles } from 'lucide-react';
import useIsMobile from '../hooks/useIsMobile';

import mbsMugImg from '../assets/images/regenerated_image_1780079303326.webp';
import studioTshirtImg from '../assets/images/regenerated_image_1780079312723.webp';
import heritageHoodieImg from '../assets/images/regenerated_image_1780079320734.webp';
import structuredPcapImg from '../assets/images/regenerated_image_1780079329116.webp';

interface Product {
  id: string;
  name: string;
  category: 'cups' | 'apparel' | 'caps';
  price: number;
  description: string;
  image: string;
  badge?: string;
  colors: { name: string; class: string; hex: string }[];
  sizes?: string[];
  mbsPrinted?: boolean;
}

interface CartItem {
  product: Product;
  selectedColor: string;
  selectedSize?: string;
  quantity: number;
}

export default function Store() {
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'cups' | 'apparel' | 'caps'>('all');
  const [logoError, setLogoError] = useState(false);
  const isMobile = useIsMobile();
  const [cart, setCart] = useState<CartItem[]>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('mbj-cart');
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          return [];
        }
      }
    }
    return [];
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [activeNotification, setActiveNotification] = useState<string | null>(null);
  const [checkoutSuccess, setCheckoutSuccess] = useState<boolean>(false);

  // Sync cart shifts and dispatch custom events
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('mbj-cart', JSON.stringify(cart));
      const count = cart.reduce((total, item) => total + item.quantity, 0);
      localStorage.setItem('mbj-cart-count', count.toString());
      window.dispatchEvent(new CustomEvent('mbj-cart-updated', { detail: { count } }));
    }
  }, [cart]);

  // Listen for externally requested open bag trigger
  useEffect(() => {
    const handleOpenCart = () => setIsCartOpen(true);
    window.addEventListener('mbj-open-cart', handleOpenCart);
    return () => window.removeEventListener('mbj-open-cart', handleOpenCart);
  }, []);

  // States for interactive selection before adding (keyed by product ID)
  const [selectedColors, setSelectedColors] = useState<Record<string, string>>({
    'mbs-mug': 'Chalk White',
    'studio-tshirt': 'Carbon Slate',
    'heritage-hoodie': 'Oatmeal Beige',
    'structured-pcap': 'Midnight Navy'
  });

  const [selectedSizes, setSelectedSizes] = useState<Record<string, string>>({
    'studio-tshirt': 'L',
    'heritage-hoodie': 'XL',
    'structured-pcap': 'Adjustable'
  });

  const [zoomPos, setZoomPos] = useState<Record<string, { x: number; y: number }>>({});

  const handleMouseMove = (productId: string, e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPos(prev => ({ ...prev, [productId]: { x, y } }));
  };

  const handleMouseLeave = (productId: string) => {
    setZoomPos(prev => {
      const updated = { ...prev };
      delete updated[productId];
      return updated;
    });
  };

  // Pure aesthetic curated merchandise
  const products: Product[] = [
    {
      id: 'mbs-mug',
      name: 'MBS Signature Ceramic Mug',
      category: 'cups',
      price: 9,
      description: 'Hand-crafted heavyweight ceramic mug with deep matte glaze and high-contrast typographic MBS hallmark stamp. Made for extended studio mornings.',
      image: mbsMugImg,
      badge: 'Premium',
      mbsPrinted: true,
      colors: [
        { name: 'Chalk White', class: 'bg-neutral-100', hex: '#f5f5f5' },
        { name: 'Silt Gray', class: 'bg-neutral-400', hex: '#a3a3a3' },
        { name: 'Ash Charcoal', class: 'bg-neutral-800', hex: '#262626' }
      ],
      sizes: ['11oz', '15oz']
    },
    {
      id: 'studio-tshirt',
      name: 'Studio Heavyweight T-Shirt',
      category: 'apparel',
      price: 13,
      description: 'Features the signature MBJ graphic on a vintage-washed charcoal base, cut with a relaxed, boxy fit and dropped shoulders for modern comfort.',
      image: studioTshirtImg,
      badge: 'Premium',
      colors: [
        { name: 'Carbon Slate', class: 'bg-zinc-800', hex: '#27272a' },
        { name: 'Neutral Oatmeal', class: 'bg-orange-50', hex: '#fffbeb' },
        { name: 'Alabaster White', class: 'bg-stone-100', hex: '#f5f5f4' }
      ],
      sizes: ['S', 'M', 'L', 'XL']
    },
    {
      id: 'heritage-hoodie',
      name: 'Heritage Structural Hoodie',
      category: 'apparel',
      price: 16,
      description: 'High-density 450GSM loopback cotton fleece. Clean structural hood without drawstrings, kangaroo pocket with flatlock seams, and relaxed modern drape.',
      image: heritageHoodieImg,
      badge: 'Premium',
      colors: [
        { name: 'Oatmeal Beige', class: 'bg-amber-100/60', hex: '#fdf6e2' },
        { name: 'Deep Sage', class: 'bg-emerald-950/80', hex: '#022c22' },
        { name: 'Ink Black', class: 'bg-black', hex: '#000000' }
      ],
      sizes: ['S', 'M', 'L', 'XL', 'XXL']
    },
    {
      id: 'structured-pcap',
      name: 'Alpha Structured P-Cap',
      category: 'caps',
      price: 7,
      description: 'Classic mid-profile construction snapback p-cap featuring continuous buckram canvas front panels, eyelets, and adjustable premium strap closure.',
      image: structuredPcapImg,
      colors: [
        { name: 'Midnight Navy', class: 'bg-blue-950', hex: '#020617' },
        { name: 'Classic Black', class: 'bg-neutral-900', hex: '#171717' },
        { name: 'Forest Green', class: 'bg-emerald-900', hex: '#064e3b' }
      ],
      sizes: ['Adjustable']
    }
  ];

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(p => p.category === selectedCategory);

  const handleColorChange = (productId: string, colorName: string) => {
    setSelectedColors(prev => ({ ...prev, [productId]: colorName }));
  };

  const handleSizeChange = (productId: string, sizeName: string) => {
    setSelectedSizes(prev => ({ ...prev, [productId]: sizeName }));
  };

  const addToCart = (product: Product) => {
    const color = selectedColors[product.id] || product.colors[0]?.name || 'Standard';
    const size = product.sizes && product.category !== 'cups' ? selectedSizes[product.id] || product.sizes[0] : undefined;

    setCart(prevCart => {
      const existingIdx = prevCart.findIndex(item => 
        item.product.id === product.id && 
        item.selectedColor === color && 
        item.selectedSize === size
      );

      if (existingIdx > -1) {
        const updated = [...prevCart];
        updated[existingIdx].quantity += 1;
        return updated;
      } else {
        return [...prevCart, { product, selectedColor: color, selectedSize: size, quantity: 1 }];
      }
    });

    setActiveNotification(product.name);
    setTimeout(() => setActiveNotification(null), 3000);
  };

  const updateCartQty = (idx: number, delta: number) => {
    setCart(prevCart => {
      const updated = [...prevCart];
      const newQty = updated[idx].quantity + delta;
      if (newQty <= 0) {
        updated.splice(idx, 1);
      } else {
        updated[idx].quantity = newQty;
      }
      return updated;
    });
  };

  const totalCartPrice = cart.reduce((total, item) => total + (item.product.price * item.quantity), 0);
  const totalCartItems = cart.reduce((total, item) => total + item.quantity, 0);

  return (
    <section id="store" className="py-24 scroll-mt-24 relative overflow-hidden bg-transparent text-neutral-900 dark:text-white border-y border-neutral-200/20 dark:border-neutral-900/40 transition-colors duration-500">
      {/* Background Ambience / Blur Overlays replicating premium high-end portfolio layout */}
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-emerald-950/20 via-neutral-950/20 to-neutral-950 pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-500" />
      <div className="absolute -top-40 right-20 w-[450px] h-[450px] bg-emerald-500/5 blur-[130px] rounded-full pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-500" />
      <div className="absolute -bottom-40 left-10 w-[550px] h-[550px] bg-emerald-500/5 blur-[150px] rounded-full pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-500" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div className="max-w-xl">
            <h2 className="flex items-center gap-2.5 text-2xl sm:text-3xl font-sans tracking-tight text-neutral-900 dark:text-white font-medium cursor-default">
              <span className="font-syne font-extrabold tracking-tight text-emerald-500">MBJ</span>
              <span>Store</span>
            </h2>
            <p className="text-sm sm:text-base text-neutral-500 dark:text-neutral-400 font-sans mt-4 font-light leading-relaxed">
              Curated limited-edition studio apparatus, daily-use vessels, and crafted uniform apparel conceptualized and produced under direct art control.
            </p>
          </div>

          {/* Filtering Widgets */}
          <div className="flex flex-wrap items-center gap-1.5">
            {(['all', 'cups', 'apparel', 'caps'] as const).map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`font-mono text-[9px] sm:text-[10px] font-bold px-3 py-1.5 rounded-full border transition-all duration-300 uppercase tracking-widest ${
                  selectedCategory === cat
                    ? 'border-emerald-500 bg-emerald-500/10 text-emerald-500 dark:text-emerald-400'
                    : 'border-neutral-200 dark:border-neutral-800 text-neutral-400 dark:text-neutral-500 hover:border-neutral-400 dark:hover:border-neutral-600'
                }`}
              >
                {cat}
              </button>
            ))}

            {/* Integrated Elegant Bag Button */}
            <button
              onClick={() => setIsCartOpen(true)}
              className="font-mono text-[9px] sm:text-[10px] font-bold px-3 py-1.5 rounded-full border border-neutral-950 bg-neutral-950 text-white dark:border-neutral-800 dark:bg-white dark:text-neutral-950 hover:bg-emerald-500 dark:hover:bg-emerald-500 hover:text-white dark:hover:text-white transition-all duration-300 uppercase tracking-widest flex items-center gap-1.5 relative"
              id="cart-trigger"
            >
              <ShoppingCart className="w-3 h-3" />
              <span>Bag</span>
              <span className="ml-1 px-1.5 py-0.5 text-[8px] font-bold rounded-full bg-emerald-500 text-white leading-none">
                {totalCartItems}
              </span>
            </button>
          </div>
        </div>

        {/* Dynamic Success Add notification bar */}
        <AnimatePresence>
          {activeNotification && (
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              className="mb-8 p-4 bg-emerald-500/10 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 text-xs sm:text-sm rounded-xl font-mono flex items-center justify-between gap-4"
            >
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-emerald-500 animate-spin" />
                <span>Added <strong>{activeNotification}</strong> to your studio cart securely.</span>
              </div>
              <button onClick={() => setActiveNotification(null)} className="hover:text-neutral-900 dark:hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredProducts.map((product, pIdx) => {
            const currentSelectedColor = selectedColors[product.id];
            const currentSelectedSize = selectedSizes[product.id] || product.sizes?.[0];

            return (
              <motion.div
                key={product.id}
                initial={isMobile ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 35, scale: 0.98 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: false, amount: 0.15 }}
                transition={isMobile ? { duration: 0.15 } : { duration: 0.5, delay: Math.min(pIdx * 0.04, 0.2) }}
                className={`group flex flex-col justify-between bg-white dark:bg-neutral-900/10 border border-neutral-100 dark:border-neutral-800/40 rounded-lg overflow-hidden transition-all duration-300 ${isMobile ? '' : 'hover:scale-[1.05] hover:z-30 hover:shadow-2xl hover:border-neutral-300 dark:hover:border-neutral-700/60'} p-3 relative origin-center`}
              >
                {/* Badge Indicator */}
                {product.badge && (
                  <span className="absolute top-6 left-6 z-10 font-mono text-[8px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded uppercase tracking-widest">
                    {product.badge}
                  </span>
                )}

                {/* Main Product Image Container with custom MBS stamp overlay */}
                <div 
                  className="relative aspect-square w-full rounded-lg overflow-hidden bg-neutral-150/10 dark:bg-neutral-950 flex items-center justify-center p-3 mb-4 group-hover:shadow-inner transition-shadow duration-300 cursor-zoom-in"
                  onMouseMove={(e) => handleMouseMove(product.id, e)}
                  onMouseLeave={() => handleMouseLeave(product.id)}
                >
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-contain rounded shadow-xs transition-transform duration-200 ease-out"
                    style={{
                      transformOrigin: zoomPos[product.id] 
                        ? `${zoomPos[product.id].x}% ${zoomPos[product.id].y}%` 
                        : 'center center',
                      transform: zoomPos[product.id] ? 'scale(1.8)' : 'scale(1)'
                    }}
                    referrerPolicy="no-referrer"
                  />
                </div>

                {/* Content Area */}
                <div className="flex-grow flex flex-col justify-between">
                  <div>
                    <div className="flex items-start justify-between gap-3 mb-1.5">
                      <h3 className="text-xs sm:text-xs font-sans font-semibold tracking-tight text-neutral-900 dark:text-white leading-tight group-hover:text-emerald-500 transition-colors">
                        {product.name}
                      </h3>
                      <span className="font-mono text-sm font-bold text-neutral-800 dark:text-neutral-200">
                        ${product.price}
                      </span>
                    </div>

                    <p className="text-[11px] sm:text-xs text-neutral-400 dark:text-neutral-500 font-sans leading-relaxed font-light mb-4">
                      {product.description}
                    </p>
                  </div>

                  {/* Controls Row */}
                  <div className="space-y-3 pt-3 border-t border-neutral-100 dark:border-neutral-800/60">
                    
                    {/* Item Sizes Selector */}
                    {product.sizes && product.category !== 'cups' && (
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-[9px] font-bold text-neutral-400 dark:text-neutral-600 uppercase tracking-widest">
                          Scale/Size
                        </span>
                        <div className="flex items-center gap-1">
                          {product.sizes.map((sz) => {
                            const isSelected = currentSelectedSize === sz;
                            return (
                              <button
                                key={sz}
                                onClick={() => handleSizeChange(product.id, sz)}
                                className={`font-mono text-[8px] font-bold px-1.5 py-0.5 rounded transition-all border ${
                                  isSelected
                                    ? 'border-emerald-500 bg-emerald-500/5 text-emerald-500'
                                    : 'border-neutral-200 dark:border-neutral-800 text-neutral-400 dark:text-neutral-500 hover:border-neutral-300'
                                }`}
                              >
                                {sz}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Add Button */}
                    <button
                      onClick={() => addToCart(product)}
                      className="w-full flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg font-mono text-[10px] font-bold uppercase tracking-widest bg-neutral-900 border border-neutral-850 text-white hover:bg-emerald-500 hover:border-emerald-500 dark:bg-white dark:text-neutral-950 dark:hover:bg-emerald-500 dark:hover:text-white transition-all cursor-pointer shadow-xs active:scale-[0.98]"
                    >
                      <ShoppingBag className="w-3.5 h-3.5" />
                      Add item to Bag
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Cart Drawer Slide Over */}
      <AnimatePresence>
        {isCartOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsCartOpen(false)}
              className="fixed inset-0 bg-neutral-950 z-50 backdrop-blur-xs"
            />

            {/* Side Sheet */}
            <motion.div
              initial={isMobile ? { opacity: 0 } : { x: '100%' }}
              animate={isMobile ? { opacity: 1 } : { x: 0 }}
              exit={isMobile ? { opacity: 0 } : { x: '100%' }}
              transition={isMobile ? { duration: 0.18, ease: 'easeOut' } : { type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white dark:bg-neutral-950 shadow-2xl z-50 flex flex-col justify-between border-l border-neutral-200 dark:border-neutral-800"
            >
              <div>
                {/* Header */}
                <div className="p-6 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ShoppingBag className="w-5 h-5 text-emerald-500" />
                    <h3 className="font-sans font-semibold text-xl tracking-tight text-neutral-900 dark:text-white">
                      Your Studio Bag
                    </h3>
                  </div>
                  <button
                    onClick={() => {
                      setIsCartOpen(false);
                      setCheckoutSuccess(false);
                    }}
                    className="p-2 rounded-full border border-neutral-200 dark:border-neutral-800 text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Body Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(100vh-280px)] space-y-6">
                  {checkoutSuccess ? (
                    <div className="py-12 text-center space-y-6">
                      <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-950/40 border border-emerald-500/25 flex items-center justify-center mx-auto text-emerald-500">
                        <Check className="w-8 h-8" />
                      </div>
                      <div className="space-y-2">
                        <h4 className="font-sans font-semibold text-lg text-neutral-900 dark:text-white">
                          Order Completed!
                        </h4>
                        <p className="text-xs text-neutral-600 dark:text-neutral-400 font-sans max-w-xs mx-auto leading-relaxed">
                          Secure checkout completed successfully. Thank you for choosing Made By Jahangir! Your request copy has been logged.
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setCart([]);
                          setCheckoutSuccess(false);
                          setIsCartOpen(false);
                        }}
                        className="px-6 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-mono text-xs font-bold tracking-widest uppercase transition-colors"
                      >
                        Keep Exploring
                      </button>
                    </div>
                  ) : cart.length === 0 ? (
                    <div className="py-16 text-center space-y-4">
                      <div className="w-12 h-12 rounded-full bg-neutral-100 dark:bg-neutral-900 flex items-center justify-center mx-auto text-neutral-500 dark:text-neutral-400">
                        <ShoppingBag className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-mono text-xs font-bold text-neutral-600 dark:text-neutral-400 uppercase tracking-widest">
                          Bag is empty
                        </p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 font-sans mt-2 font-light">
                          Add premium garments or custom MBS vessels to proceed.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="divide-y divide-neutral-100 dark:divide-neutral-800/40">
                      {cart.map((item, idx) => (
                        <div key={idx} className="py-4 flex gap-4 first:pt-0 last:pb-0">
                          {/* Mini Thumbnail */}
                          <div className="w-16 h-16 rounded-lg bg-neutral-100 dark:bg-neutral-900 overflow-hidden flex-shrink-0 border border-neutral-250/20 relative p-1">
                            <img src={item.product.image} alt={item.product.name} className="w-full h-full object-contain" />
                            {item.product.mbsPrinted && (
                              <div className="absolute inset-0 flex items-center justify-center bg-neutral-950/60 text-[7px] font-mono font-bold text-white tracking-widest uppercase">
                                MBS
                              </div>
                            )}
                          </div>

                          {/* Detail fields */}
                          <div className="flex-grow flex flex-col justify-between">
                            <div>
                              <div className="flex items-start justify-between gap-4">
                                <h4 className="text-xs sm:text-sm font-sans font-semibold text-neutral-900 dark:text-white leading-tight">
                                  {item.product.name}
                                </h4>
                                <span className="font-mono text-xs font-bold text-neutral-900 dark:text-white text-right">
                                  ${item.product.price * item.quantity}
                                </span>
                              </div>
                              
                              <p className="text-[10px] font-mono text-neutral-500 dark:text-neutral-400 mt-1 flex items-center gap-2">
                                <span>{item.selectedColor}</span>
                                {item.selectedSize && (
                                  <>
                                    <span className="h-1 w-1 bg-neutral-300 dark:bg-neutral-700 rounded-full" />
                                    <span>{item.selectedSize}</span>
                                  </>
                                )}
                              </p>
                            </div>

                            {/* Qty Manager */}
                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-neutral-100/40 dark:border-neutral-800/20">
                              <span className="font-mono text-[9px] text-neutral-400 dark:text-neutral-500 uppercase">Qty</span>
                              <div className="flex items-center gap-2.5">
                                <button
                                  onClick={() => updateCartQty(idx, -1)}
                                  className="p-1 rounded bg-neutral-50 dark:bg-neutral-900 border border-neutral-200/50 dark:border-neutral-800/50 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                                >
                                  <Minus className="w-3 h-3 text-neutral-500" />
                                </button>
                                <span className="font-mono text-xs font-bold min-w-[12px] text-center text-neutral-800 dark:text-neutral-200">
                                  {item.quantity}
                                </span>
                                <button
                                  onClick={() => updateCartQty(idx, 1)}
                                  className="p-1 rounded bg-neutral-50 dark:bg-neutral-900 border border-neutral-200/50 dark:border-neutral-800/50 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                                >
                                  <Plus className="w-3 h-3 text-neutral-500" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Checkout Summary Footer */}
              {!checkoutSuccess && (
                <div className="p-6 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/10 mb-2">
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400 font-mono uppercase tracking-widest">
                      <span>Active Items</span>
                      <span>{totalCartItems}</span>
                    </div>
                    <div className="flex items-center justify-between text-neutral-900 dark:text-white">
                      <span className="font-sans font-semibold text-base">Estimated Total</span>
                      <span className="font-mono text-lg font-bold">${totalCartPrice}</span>
                    </div>
                  </div>

                  <button
                    disabled={cart.length === 0}
                    onClick={() => {
                      setCheckoutSuccess(true);
                    }}
                    className="w-full py-4 rounded-xl bg-emerald-500 text-white font-mono text-xs font-bold uppercase tracking-widest hover:bg-emerald-600 disabled:opacity-40 disabled:hover:bg-emerald-500 transition-colors cursor-pointer shadow-md select-none"
                  >
                    Proceed to Checkout
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </section>
  );
}
